/**
 * API client for FCC License Manager backend
 * Handles all HTTP requests with authentication
 */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import type { APIError } from '../types'

// Create axios instance with base configuration
// Requests use a relative base so they go through the Vite proxy in dev
// (localhost:5173/api/v1/... → backend localhost:8000/api/v1/...).
// VITE_API_URL is only used by vite.config.ts to set the proxy target.
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests (for JWT in httpOnly cookies)
})

// Request interceptor - add auth token if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Access token is in httpOnly cookie, no need to add it manually
    // The browser will automatically include it with withCredentials: true
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Endpoints that use HTTP 401 to signal a failed *credential / step-up* attempt
// (wrong password, bad MFA code, passkey mismatch) rather than an invalid session.
// These are handled inline by the calling component, so a 401 from them must NOT
// trigger a global logout — doing so kicked users to the login screen on e.g. a
// mistyped admin re-auth password instead of showing an inline error.
const STEP_UP_AUTH_PATHS = [
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/passkey/authenticate',
  '/auth/passkey/register',
  '/users/admin-reauth',
]

// Header the backend uses to hand back a slid-forward access token. It is only
// sent for requests that authenticated with an Authorization: Bearer header —
// the dev/Vite-proxy fallback path. Cookie-authenticated requests get the
// renewed token in the httpOnly cookie instead, which the browser applies on its
// own and JavaScript never sees.
const RENEWED_TOKEN_HEADER = 'x-renewed-access-token'

// Response interceptor - handle common errors
api.interceptors.response.use(
  (response) => {
    // Keep the in-memory Bearer fallback in step with the renewed token; without
    // this it would keep replaying the original token until it expired, and the
    // user would still be logged out mid-session in dev.
    const renewed = response.headers?.[RENEWED_TOKEN_HEADER]
    if (typeof renewed === 'string' && renewed) {
      api.defaults.headers.common['Authorization'] = `Bearer ${renewed}`
    }
    return response
  },
  (error: AxiosError<APIError>) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Only treat a 401 as "session invalid → log out" for normal authenticated
      // requests. Step-up/credential endpoints report their own 401s inline.
      const url = error.config?.url ?? ''
      const isStepUpAuth = STEP_UP_AUTH_PATHS.some((path) => url.includes(path))
      if (!isStepUpAuth) {
        // Unauthorized - clear auth state and redirect to login
        // This will be handled by the auth context
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
      }
    }

    // Handle forbidden errors
    if (error.response?.status === 403) {
      const detail = (error.response.data as { detail?: string })?.detail
      if (detail === 'password_change_required') {
        // Redirect to profile page so the user can change their password
        window.dispatchEvent(new CustomEvent('auth:password-change-required'))
      } else {
        console.error('Access forbidden:', error.response.data)
      }
    }

    // Handle validation errors
    if (error.response?.status === 422) {
      console.error('Validation error:', error.response.data)
    }

    // Handle server errors
    if (error.response?.status && error.response.status >= 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

/**
 * POST a multipart/form-data body (file uploads).
 *
 * This exists because the shared `api` instance sets a default
 * `Content-Type: application/json`, and axios applies its `transformRequest`
 * step BEFORE it notices the body is a FormData. Seeing a JSON content type it
 * runs `JSON.stringify(formDataToJSON(data))`, which destroys the multipart
 * body — the request then arrives as JSON, the server's multipart parser never
 * runs, and the endpoint reports the file field as simply missing. That is
 * exactly how the company-logo upload failed with `file: Field required` for
 * every user, in every browser, since the feature shipped.
 *
 * Naming the content type here keeps the FormData intact through
 * `transformRequest`; axios then clears the header itself so the browser can
 * write a real multipart boundary.
 *
 * Always upload through this helper rather than calling `api.post` with a
 * FormData directly — the failure is silent at the type level, and a backend
 * test cannot see it because it never goes through axios.
 */
export function postMultipart<T = unknown>(
  url: string,
  form: FormData,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return api.post<T>(url, form, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  })
}

/**
 * Extract error message from API error response
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>
    const detail = axiosError.response?.data?.detail
    if (detail) {
      // FastAPI 422 validation errors return detail as an array of error objects
      if (Array.isArray(detail)) {
        return detail
          .map((e: { msg?: string; loc?: string[] }) =>
            e.loc ? `${e.loc.slice(1).join('.')}: ${e.msg}` : (e.msg ?? String(e))
          )
          .join('; ')
      }
      return detail as string
    }
    if (axiosError.message) {
      return axiosError.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}

export default api
